import { useState, useEffect, useCallback, useRef } from 'react'
import { ArrowRight, CheckCircle, Building2, Users } from 'lucide-react'
import type { ChatMessage } from '../types'
import {
  type SessionAroundResponse,
  type ThreadEvent,
  type PaginatedThreadEventsResponse,
  type ConferenceEventData,
  type ConversationProcessingResultEventData,
  type HandoffInitiatedEventData,
  type HandoffActionExecutionEventData,
  type CallStartedEventData,
  type CallEndedEventData,
  type CallRecordingEventData,
  type SmsMessageReceivedEventData,
  type SmsMessageSentEventData,
  type EmailMessageReceivedEventData,
  type EmailMessageSentEventData,
  type HandoffCompletedEventData,
  type SystemNotificationEventData,
  type PropertyDetectedEventData,
  type ContactGroupDetectedEventData,
  type PushIntegrationInitiatedEventData,
  type PushIntegrationCompletedEventData,
  type PushIntegrationFailedEventData,
  formatUSPhoneForDisplay,
  formatContactGroupList,
  getActionSubtypeLabel,
  getPushIntegrationConfig,
  isSuppressionSystemNotification,
} from '@super/shared-types'
import { threadEventsApiClient } from '@/lib/api'
import { useAppAuth } from '@/contexts/app-auth.context'

interface UseThreadEventsReturn {
  messages: ChatMessage[]
  hasMore: boolean
  loading: boolean
  loadingMore: boolean
  cursor: string | null
  loadMoreMessages: () => Promise<void>
  loadEventsAroundMessage: (eventId: string) => Promise<void>
  loadEventsAroundSession: (sessionId: string) => Promise<string>
  refreshEvents: () => Promise<void>
  // Newer messages support (for deeplinks)
  hasMoreNewer: boolean
  nextCursor: string | null
  isDeeplinked: boolean
  loadMoreNewerMessages: () => Promise<void>
  // Session deeplink scroll target (resolved from sessionId)
  sessionScrollTarget: string | null
}

// Transform thread events to chat messages
export function transformEventToMessage(event: ThreadEvent): ChatMessage {
  const baseMessage = {
    id: event.id,
    threadId: '', // Will be set by caller
    createdAt: new Date(event.eventTime),
    metadata: null,
    sessionId: event.sessionId,
  }

  switch (event.eventType) {
    case 'CALL_STARTED':
      const callStartedData = event.eventData as CallStartedEventData
      return {
        ...baseMessage,
        type: 'call_start',
        content: `Call started from ${callStartedData.from}`,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: callStartedData,
      }

    case 'CALL_ENDED':
      const callEndedData = event.eventData as CallEndedEventData
      // Duration is 0 when the end time was not known at publish; say
      // "Call ended" rather than "after 0 minutes".
      const callEndedMinutes = Math.round((callEndedData.duration ?? 0) / 60)
      return {
        ...baseMessage,
        type: 'call_end',
        content:
          callEndedMinutes > 0
            ? `Call ended after ${callEndedMinutes} ${callEndedMinutes === 1 ? 'minute' : 'minutes'}`
            : 'Call ended',
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: callEndedData,
      }

    case 'CALL_RECORDING':
      const recordingData = event.eventData as CallRecordingEventData
      return {
        ...baseMessage,
        type: 'recording',
        content: 'Call recording available',
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: recordingData,
      }

    case 'MESSAGE_RECEIVED':
      const receivedMessageData = event.eventData as SmsMessageReceivedEventData
      return {
        ...baseMessage,
        type: 'text',
        content: receivedMessageData.messageText,
        role: 'user',
        sender: {
          id: receivedMessageData.senderId || 'user',
          name: receivedMessageData.senderName || 'User',
          type: receivedMessageData.senderType,
        },
      }

    case 'EMAIL_MESSAGE_RECEIVED':
      const emailReceivedData = event.eventData as EmailMessageReceivedEventData
      return {
        ...baseMessage,
        type: 'text',
        content: emailReceivedData.messageText,
        role: 'user',
        sender: {
          id: emailReceivedData.senderId || 'user',
          name: emailReceivedData.senderName || 'User',
          type: emailReceivedData.senderType,
        },
        data: emailReceivedData,
      }

    case 'MESSAGE_SENT':
      const sentMessageData = event.eventData as SmsMessageSentEventData
      return {
        ...baseMessage,
        type: 'text',
        content: sentMessageData.messageText,
        role: 'assistant',
        sender: {
          id: sentMessageData.senderId || 'assistant',
          name: sentMessageData.senderName || 'Assistant',
          type: sentMessageData.senderType,
        },
      }

    case 'EMAIL_MESSAGE_SENT':
      const emailSentData = event.eventData as EmailMessageSentEventData
      return {
        ...baseMessage,
        type: 'text',
        content: emailSentData.messageText,
        role: 'assistant',
        sender: {
          id: emailSentData.senderId || 'assistant',
          name: emailSentData.senderName || 'Assistant',
          type: emailSentData.senderType,
        },
        data: emailSentData,
      }

    case 'HANDOFF_INITIATED':
      const handoffInitiatedData = event.eventData as HandoffInitiatedEventData
      return {
        ...baseMessage,
        type: 'system',
        content: `Handoff Scenario: ${handoffInitiatedData.workflowName} - "${handoffInitiatedData.handoffReason}"`,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: handoffInitiatedData,
        icon: ArrowRight,
      }

    case 'HANDOFF_COMPLETED':
      const handoffCompletedData = event.eventData as HandoffCompletedEventData
      return {
        ...baseMessage,
        type: 'system',
        content: `Handoff ${handoffCompletedData.success ? 'completed successfully' : 'failed'}: ${handoffCompletedData.workflowName}`,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: handoffCompletedData,
      }

    case 'SYSTEM_NOTIFICATION':
      const systemData = event.eventData as SystemNotificationEventData
      return {
        ...baseMessage,
        type: 'system',
        content: isSuppressionSystemNotification(systemData)
          ? 'AI response suppressed'
          : systemData.message,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: systemData,
      }

    case 'CONFERENCE_TRANSCRIPT':
      const conferenceData = event.eventData as ConferenceEventData
      return {
        ...baseMessage,
        type: 'system',
        content: `Call transcript available for ${conferenceData.callId}`,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: conferenceData,
        hidden: true,
      }

    case 'CALL_PROCESSING_RESULT':
      const processingData =
        event.eventData as ConversationProcessingResultEventData & {
          summaryResponse?: {
            title: string
            summary: string
            callerName?: string
            isResolved: boolean
            nextSteps?: string
            sentiment?: string
            links: Array<{ title: string; value: string }>
          }
        }

      // Transform old summaryResponse to new userSummary structure for backward compatibility
      const userSummary =
        processingData.userSummary ||
        (processingData.summaryResponse
          ? {
              title: processingData.summaryResponse.title,
              summary: processingData.summaryResponse.summary,
              contactDetails: {
                name: processingData.summaryResponse.callerName,
              },
              isResolved: processingData.summaryResponse.isResolved,
              nextSteps: processingData.summaryResponse.nextSteps,
              sentiment: processingData.summaryResponse.sentiment,
              links: processingData.summaryResponse.links,
            }
          : undefined)

      return {
        ...baseMessage,
        type: 'conversation_processing_summary',
        content: userSummary?.summary || 'Call processed',
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: {
          ...processingData,
          userSummary,
        },
      }

    case 'SMS_PROCESSING_RESULT':
      const smsProcessingData =
        event.eventData as ConversationProcessingResultEventData

      return {
        ...baseMessage,
        type: 'conversation_processing_summary',
        content: smsProcessingData.userSummary?.summary || 'SMS processed',
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: smsProcessingData,
      }

    case 'EMAIL_PROCESSING_RESULT':
      const emailProcessingData =
        event.eventData as ConversationProcessingResultEventData

      return {
        ...baseMessage,
        type: 'conversation_processing_summary',
        content: emailProcessingData.userSummary?.summary || 'Email processed',
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: emailProcessingData,
      }

    case 'HANDOFF_ACTION_EXECUTION':
      const actionData = event.eventData as HandoffActionExecutionEventData

      // Create simple text content as fallback (for accessibility)
      // SystemMessage component will render rich content with inline icons from actionData
      let content = ''

      if (actionData.actionResults && actionData.actionResults.length > 0) {
        const actions: string[] = []

        // Add phone actions to content
        actionData.actionResults
          .filter(result => result.actionType === 'PHONE_CALL')
          .forEach(result => {
            const phoneNumber = result.parameters?.number
            const phoneDisplay = phoneNumber
              ? formatUSPhoneForDisplay(phoneNumber as string)
              : 'Unknown number'
            actions.push(`Transfer attempted to ${phoneDisplay}`)
          })

        // Add email actions to content
        actionData.actionResults
          .filter(result => result.actionType === 'EMAIL')
          .forEach(result => {
            const recipients = result.parameters?.to
            let recipientText = 'unknown recipients'

            if (Array.isArray(recipients)) {
              recipientText = recipients.join(', ')
            } else if (typeof recipients === 'string') {
              recipientText = recipients
            }

            actions.push(`Notify ${recipientText}`)
          })

        content =
          actions.length > 0
            ? actions.join(', ')
            : `Handoff actions ${actionData.overallSuccess ? 'completed successfully' : 'failed'} for ${actionData.workflowName}`
      } else {
        content = `Handoff actions ${actionData.overallSuccess ? 'completed successfully' : 'failed'} for ${actionData.workflowName}`
      }

      return {
        ...baseMessage,
        type: 'action_execution',
        content,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: actionData,
        icon: CheckCircle,
      }

    case 'PROPERTY_DETECTED':
      const propertyData = event.eventData as PropertyDetectedEventData
      const propertyList = propertyData.properties
        .map(p => {
          const name = p.propertyName || p.propertyAddress || p.propertyId
          if (!name) return null
          // Add unit if present
          return p.unit ? `${name}, Unit ${p.unit}` : name
        })
        .filter(Boolean)
        .join(', ')

      return {
        ...baseMessage,
        type: 'system',
        content: `Property detected: ${propertyList || 'Unknown property'}`,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: propertyData,
        icon: Building2,
        sessionId: propertyData.sessionId,
      }

    case 'CONTACT_GROUP_DETECTED': {
      const contactGroupData = event.eventData as ContactGroupDetectedEventData
      const groupList = formatContactGroupList(contactGroupData.contactGroups)

      return {
        ...baseMessage,
        type: 'system',
        content: `Contact group detected: ${groupList}`,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: contactGroupData,
        icon: Users,
        sessionId: contactGroupData.sessionId,
      }
    }

    case 'PUSH_INTEGRATION_INITIATED': {
      const data = event.eventData as PushIntegrationInitiatedEventData

      const config = getPushIntegrationConfig(
        data.integrationType,
        data.actionSubtype,
      )
      const integrationName = config?.integrationName ?? 'Unknown Integration'

      const actionLabel = data.actionSubtype
        ? getActionSubtypeLabel(data.actionSubtype)
        : null
      // Format: "Creating Work Order in Yardi Breeze for Maintenance - all..."
      // Consistent with handoff events pattern using workflowName
      const content = actionLabel
        ? `Creating ${actionLabel} in ${integrationName} for ${data.workflowName}...`
        : `Processing in ${integrationName} for ${data.workflowName}...`
      return {
        ...baseMessage,
        type: 'system',
        content,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data,
      }
    }

    case 'PUSH_INTEGRATION_COMPLETED': {
      const data = event.eventData as PushIntegrationCompletedEventData

      const config = getPushIntegrationConfig(
        data.integrationType,
        data.actionSubtype,
      )
      const integrationName = config?.integrationName ?? 'Unknown Integration'

      if (!config) {
        // Fallback for unknown integrations
        const content = `Completed in ${integrationName} for ${data.workflowName}`
        return {
          ...baseMessage,
          type: 'system',
          content,
          role: 'system',
          sender: { id: 'system', name: 'System' },
          data,
        }
      }

      // Extract result info using registry methods instead of hardcoding
      const responseData = data.responseData as
        | Record<string, unknown>
        | undefined
      const resultId = responseData
        ? config.getResultId(responseData)
        : undefined
      const resultUrl =
        responseData && config.getResultUrl
          ? config.getResultUrl(responseData)
          : undefined
      const resultLabel = responseData
        ? config.getResultLabel(responseData)
        : null

      // Format message
      const actionLabel =
        resultLabel ||
        (data.actionSubtype ? getActionSubtypeLabel(data.actionSubtype) : null)
      const content = actionLabel
        ? `${actionLabel} created in ${integrationName} for ${data.workflowName}`
        : `Completed in ${integrationName} for ${data.workflowName}`

      // IMPORTANT: Always create _display (even without URL) for tooltip
      const displayData = {
        ...data,
        _display: {
          linkUrl: resultUrl, // May be undefined
          linkText: integrationName,
          tooltipText:
            resultId && actionLabel
              ? `${actionLabel} #${resultId}`
              : resultId
                ? `#${resultId}`
                : actionLabel || undefined,
          contentBeforeLink: actionLabel
            ? `${actionLabel} created in `
            : `Completed in `,
          contentAfterLink: ` for ${data.workflowName}`,
        },
      }

      return {
        ...baseMessage,
        type: 'system',
        content,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data: displayData,
      }
    }

    case 'PUSH_INTEGRATION_FAILED': {
      const data = event.eventData as PushIntegrationFailedEventData

      const config = getPushIntegrationConfig(
        data.integrationType,
        data.actionSubtype,
      )
      const integrationName = config?.integrationName ?? 'Unknown Integration'

      const actionLabel = data.actionSubtype
        ? getActionSubtypeLabel(data.actionSubtype)
        : null
      // Format: "Work Order creation failed in Yardi Breeze for Maintenance - all - Error message"
      // Consistent with handoff events pattern using workflowName
      const content = actionLabel
        ? `${actionLabel} creation failed in ${integrationName} for ${data.workflowName} - ${data.error}`
        : `Failed in ${integrationName} for ${data.workflowName} - ${data.error}`
      return {
        ...baseMessage,
        type: 'system',
        content,
        role: 'system',
        sender: { id: 'system', name: 'System' },
        data,
      }
    }

    default:
      return {
        ...baseMessage,
        type: 'text',
        content: 'Unknown event type',
        role: 'system',
        sender: { id: 'system', name: 'System' },
      }
  }
}

export const useThreadEvents = (
  threadId: string | null,
  sessionId?: string | null,
): UseThreadEventsReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  // Newer messages support (for deeplinks)
  const [hasMoreNewer, setHasMoreNewer] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  // isDeeplinked: state that drives UI rendering (bidirectional scroll indicators, etc.)
  // isDeeplinkedRef: ref for synchronous checks in effects to prevent the initial load
  // from overwriting deeplinked messages (state updates are batched/async so can't be
  // read synchronously in effect guards)
  const [isDeeplinked, setIsDeeplinked] = useState(false)
  const isDeeplinkedRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [sessionScrollTarget, setSessionScrollTarget] = useState<string | null>(
    null,
  )
  const { isSuperAdmin, currentCompany } = useAppAuth()

  // Helper to keep isDeeplinked state and ref in sync
  const setDeeplinked = useCallback((value: boolean) => {
    setIsDeeplinked(value)
    isDeeplinkedRef.current = value
  }, [])

  const fetchThreadEvents = useCallback(
    async (useCursor?: string | null) => {
      if (!threadId) return

      try {
        let data: PaginatedThreadEventsResponse

        const companyId = isSuperAdmin ? currentCompany?.id : undefined

        if (useCursor) {
          // Load older messages using cursor
          if (isSuperAdmin) {
            data = await threadEventsApiClient.getAdminThreadEventsBefore(
              threadId,
              useCursor,
              20,
              companyId,
            )
          } else {
            data = await threadEventsApiClient.getThreadEventsBefore(
              threadId,
              useCursor,
              20,
            )
          }
        } else {
          // Initial load
          const params = {
            limit: 50,
            direction: 'before' as const,
            ...(isSuperAdmin && companyId && { companyId }),
          }

          if (isSuperAdmin) {
            data = await threadEventsApiClient.getAdminThreadEvents(
              threadId,
              params,
            )
          } else {
            data = await threadEventsApiClient.getThreadEvents(threadId, params)
          }
        }

        // Transform events to messages
        const newMessages = data.data.map((event: ThreadEvent) => ({
          ...transformEventToMessage(event),
          threadId,
        }))

        if (useCursor) {
          // Append older messages (infinite scroll) - reverse since API returns newest first
          setMessages(prev => [...newMessages.reverse(), ...prev])
        } else {
          // Initial load - reverse since API returns newest first, but we want oldest first
          setMessages(newMessages.reverse())
          // Reset deeplink state on normal load
          setDeeplinked(false)
          setHasMoreNewer(false)
          setNextCursor(null)
        }

        setHasMore(data.pagination.hasNext)
        setCursor(data.pagination.previousCursor || null)
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          // Clear messages on error (e.g., thread doesn't belong to company, access denied)
          setMessages([])
          setHasMore(false)
          setCursor(null)
        }
        // Don't re-throw - handle gracefully so loading state can be cleared
      }
    },
    [threadId, isSuperAdmin, currentCompany?.id],
  )

  // Load events around the first event of a session (for sessionId deeplinks)
  const loadEventsAroundSession = useCallback(
    async (sid: string): Promise<string> => {
      if (!threadId) return ''

      isDeeplinkedRef.current = true

      try {
        setLoading(true)
        const companyId = isSuperAdmin ? currentCompany?.id : undefined

        let data: SessionAroundResponse

        if (isSuperAdmin) {
          data = await threadEventsApiClient.getAdminThreadEventsAroundSession(
            threadId,
            sid,
            50,
            companyId,
          )
        } else {
          data = await threadEventsApiClient.getThreadEventsAroundSession(
            threadId,
            sid,
            50,
          )
        }

        const newMessages = data.data.map((event: ThreadEvent) => ({
          ...transformEventToMessage(event),
          threadId,
        }))

        setMessages(newMessages)
        setHasMore(data.pagination.hasPrevious)
        setCursor(data.pagination.previousCursor || null)
        setHasMoreNewer(data.pagination.hasNext)
        setNextCursor(data.pagination.nextCursor || null)
        setDeeplinked(true)
        // Set scroll target in the same synchronous block as setMessages
        // so React batches them into a single render — prevents scroll-to-bottom
        // from firing in an intermediate render where messages are loaded but scrollTo is still undefined
        const targetEventId = data.included.targetEventId
        setSessionScrollTarget(targetEventId)

        return targetEventId
      } catch (error) {
        console.error('Failed to load events around session:', error)
        setDeeplinked(false)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [threadId, isSuperAdmin, currentCompany?.id],
  )

  // Track previous threadId to distinguish thread changes from fetchThreadEvents ref changes
  const prevInitialLoadThreadRef = useRef<string | null>(null)

  // Initial load when thread changes
  useEffect(() => {
    if (!threadId) {
      setMessages([])
      setHasMore(true)
      setCursor(null)
      setLoading(false)
      setDeeplinked(false)
      setHasMoreNewer(false)
      setNextCursor(null)
      setSessionScrollTarget(null)
      prevInitialLoadThreadRef.current = null
      return
    }

    // Only reset deeplink state when the thread actually changes
    // (not when fetchThreadEvents ref changes due to currentCompany resolving)
    const threadChanged = prevInitialLoadThreadRef.current !== threadId
    prevInitialLoadThreadRef.current = threadId

    if (threadChanged) {
      setDeeplinked(false)
      setSessionScrollTarget(null)
    }

    // Skip initial load if we already have deeplinked messages for this thread
    // (prevents overwriting when fetchThreadEvents ref changes due to currentCompany)
    if (isDeeplinkedRef.current) return

    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    // If sessionId is provided, load around session's first event instead of recent events
    if (sessionId) {
      loadEventsAroundSession(sessionId).catch(() => {
        // Session not found — fall back to normal load
        // Re-set loading since loadEventsAroundSession's finally already set it to false
        setLoading(true)
        fetchThreadEvents().finally(() => setLoading(false))
      })
      return
    }

    const loadInitialEvents = async () => {
      setLoading(true)
      try {
        await fetchThreadEvents()
      } finally {
        setLoading(false)
      }
    }

    loadInitialEvents()

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      setLoading(false)
    }
  }, [threadId, fetchThreadEvents, sessionId, loadEventsAroundSession])

  const loadMoreMessages = useCallback(async () => {
    if (!threadId || loadingMore || !hasMore || !cursor) {
      return
    }

    setLoadingMore(true)
    await fetchThreadEvents(cursor)
    setLoadingMore(false)
  }, [threadId, loadingMore, hasMore, cursor, fetchThreadEvents])

  const refreshEvents = useCallback(async () => {
    if (!threadId) return

    setLoading(true)
    setCursor(null)
    setDeeplinked(false)
    setHasMoreNewer(false)
    setNextCursor(null)
    await fetchThreadEvents()
    setLoading(false)
  }, [threadId, fetchThreadEvents])

  // Load newer messages (forwards scrolling when deeplinked)
  const loadMoreNewerMessages = useCallback(async () => {
    if (
      !threadId ||
      loadingMore ||
      !hasMoreNewer ||
      !nextCursor ||
      !isDeeplinked
    ) {
      return
    }

    try {
      setLoadingMore(true)
      const companyId = isSuperAdmin ? currentCompany?.id : undefined

      let data: PaginatedThreadEventsResponse

      // Use the main getThreadEvents endpoint with direction='after' to load newer messages
      const params = {
        limit: 20,
        cursor: nextCursor,
        direction: 'after' as const,
        ...(isSuperAdmin && companyId && { companyId }),
      }

      if (isSuperAdmin) {
        data = await threadEventsApiClient.getAdminThreadEvents(
          threadId,
          params,
        )
      } else {
        data = await threadEventsApiClient.getThreadEvents(threadId, params)
      }

      // Transform events to messages
      const newMessages = data.data.map((event: ThreadEvent) => ({
        ...transformEventToMessage(event),
        threadId,
      }))

      // API returns newer messages in reverse order (newest first), so reverse to chronological
      // Append newer messages to the end in chronological order (oldest first)
      setMessages(prev => [...prev, ...newMessages.reverse()])

      // Update pagination state for newer messages
      setHasMoreNewer(data.pagination.hasNext)
      setNextCursor(data.pagination.nextCursor || null)
    } catch (error) {
      console.error('Failed to load newer messages:', error)
      // Don't clear state on error - allow retry
    } finally {
      setLoadingMore(false)
    }
  }, [
    threadId,
    loadingMore,
    hasMoreNewer,
    nextCursor,
    isDeeplinked,
    isSuperAdmin,
    currentCompany?.id,
  ])

  // Load events around a specific message (for deeplinks)
  const loadEventsAroundMessage = useCallback(
    async (eventId: string) => {
      if (!threadId) return

      // Set ref (not state) early — we need this synchronously before the async fetch
      // to guard against the initial load effect overwriting our messages mid-flight
      isDeeplinkedRef.current = true

      try {
        setLoading(true)
        const companyId = isSuperAdmin ? currentCompany?.id : undefined

        let data: PaginatedThreadEventsResponse

        if (isSuperAdmin) {
          data = await threadEventsApiClient.getAdminThreadEventsAroundMessage(
            threadId,
            eventId,
            50, // Load 25 before + 25 after
            companyId,
          )
        } else {
          data = await threadEventsApiClient.getThreadEventsAroundMessage(
            threadId,
            eventId,
            50,
          )
        }

        // Transform events to messages
        const newMessages = data.data.map((event: ThreadEvent) => ({
          ...transformEventToMessage(event),
          threadId,
        }))

        // Replace messages with the new set centered around target
        // findAroundSequence already returns data in chronological order (oldest first)
        setMessages(newMessages)
        // hasMore should indicate if there are more OLDER messages (for infinite scroll)
        // hasPrevious means there are more messages before (older), which is what we need
        setHasMore(data.pagination.hasPrevious)
        setCursor(data.pagination.previousCursor || null)
        // Track newer messages for forward infinite scrolling
        setHasMoreNewer(data.pagination.hasNext)
        setNextCursor(data.pagination.nextCursor || null)
        setDeeplinked(true) // Mark as deeplinked to enable bidirectional scrolling
      } catch (error) {
        console.error('Failed to load events around message:', error)
        setDeeplinked(false) // Reset on failure so initial load can proceed
        // Don't clear messages — the effect will re-evaluate on next render and
        // hit the fallback branch (incremental onLoadMore) if hasMore is true
        throw error
      } finally {
        setLoading(false)
      }
    },
    [threadId, isSuperAdmin, currentCompany?.id],
  )

  return {
    messages,
    hasMore,
    loading,
    loadingMore,
    cursor,
    loadMoreMessages,
    loadEventsAroundMessage,
    loadEventsAroundSession,
    refreshEvents,
    hasMoreNewer,
    nextCursor,
    isDeeplinked,
    loadMoreNewerMessages,
    sessionScrollTarget,
  }
}
